import { useEffect } from 'react'

/** Anything smaller is browser chrome (URL bar) resizing, not a keyboard. */
const KEYBOARD_MIN_PX = 150

const isEditable = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' &&
    !['button', 'checkbox', 'radio', 'range', 'color', 'file', 'submit', 'reset'].includes((el as HTMLInputElement).type)))

export interface KeyboardState {
  open: boolean
  /** Pixels of the layout viewport the keyboard covers (iOS); 0 when the page is resized for it (Android). */
  inset: number
}

/**
 * Reads the on-screen keyboard from the viewport sizes.
 *
 * - iOS Safari overlays the keyboard: the visual viewport shrinks, the layout
 *   viewport doesn't, so the gap between them is the keyboard.
 * - Android Chrome with `interactive-widget=resizes-content` shrinks the page
 *   itself, so bottom-pinned UI already sits above the keyboard; we only need
 *   to know it's open, by comparing against the tallest height seen at this width.
 */
export function readKeyboard(
  win: { innerHeight: number; innerWidth: number; visualViewport: { height: number; offsetTop: number } | null },
  baselineHeight: number,
  focused: Element | null,
): KeyboardState {
  if (!isEditable(focused)) return { open: false, inset: 0 }
  const vv = win.visualViewport
  const overlay = vv ? Math.max(0, win.innerHeight - vv.height - vv.offsetTop) : 0
  if (overlay >= KEYBOARD_MIN_PX) return { open: true, inset: overlay }
  if (baselineHeight - win.innerHeight >= KEYBOARD_MIN_PX) return { open: true, inset: 0 }
  return { open: false, inset: 0 }
}

/**
 * Publishes the keyboard on <html>: `--kb` (px to lift bottom-pinned UI by)
 * and `data-keyboard="open"`, so CSS can move sheets and hide the tab bar.
 * Mount once, in the app shell.
 */
export function useKeyboardInset() {
  useEffect(() => {
    const root = document.documentElement
    const vv = window.visualViewport
    let baseline = { width: window.innerWidth, height: window.innerHeight }
    let frame = 0

    const update = () => {
      frame = 0
      if (window.innerWidth !== baseline.width) baseline = { width: window.innerWidth, height: window.innerHeight }
      else if (window.innerHeight > baseline.height) baseline.height = window.innerHeight
      const { open, inset } = readKeyboard(window, baseline.height, document.activeElement)
      root.style.setProperty('--kb', `${inset}px`)
      if (open) root.dataset.keyboard = 'open'
      else delete root.dataset.keyboard
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    update()
    vv?.addEventListener('resize', schedule)
    vv?.addEventListener('scroll', schedule)
    window.addEventListener('resize', schedule)
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      vv?.removeEventListener('resize', schedule)
      vv?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
      root.style.removeProperty('--kb')
      delete root.dataset.keyboard
    }
  }, [])
}
